// OPFS-backed persistent store for the web build's mod staging tree.
//
// The mod staging / downloads / temp dirs live under the virtual userData
// path (`/vortex/userData/...`). fsa-core routes that subtree here instead
// of the in-memory overlay so installed mods survive reloads and large mods
// stream to disk rather than sitting in tab memory.
//
// Storage is the Origin Private File System: real, origin-private,
// browser-managed local storage. The virtual path is mirrored as an OPFS
// directory tree under the storage root (segments used verbatim).
const { Buffer } = require("buffer");

function nodeError(code, errno, filePath, message) {
    const err = new Error(`${code}: ${message}, '${filePath}'`);
    err.code = code;
    err.errno = errno;
    err.path = filePath;
    return err;
}
const enoent = (p) => nodeError("ENOENT", -2, p, "no such file or directory");

let rootPromise = null;
let unavailable = false;

async function getRoot() {
    if (unavailable) {
        return null;
    }
    if (rootPromise === null) {
        rootPromise = (async () => {
            if (
                typeof navigator === "undefined" ||
                navigator.storage === undefined ||
                typeof navigator.storage.getDirectory !== "function"
            ) {
                unavailable = true;
                return null;
            }
            try {
                return await navigator.storage.getDirectory();
            } catch (err) {
                console.warn("[vortex-web] OPFS unavailable", err);
                unavailable = true;
                return null;
            }
        })();
    }
    return rootPromise;
}

async function available() {
    return (await getRoot()) !== null;
}

function segmentsOf(normPath) {
    return normPath.split("/").filter((seg) => seg.length > 0);
}

// resolves the directory handle containing `segments[last]`; when create is
// true, intermediate dirs are created
async function resolveParent(root, segments, create, filePath) {
    let dir = root;
    for (let i = 0; i < segments.length - 1; i++) {
        try {
            dir = await dir.getDirectoryHandle(segments[i], { create });
        } catch (err) {
            throw enoent(filePath);
        }
    }
    return dir;
}

async function getFileHandle(normPath, create) {
    const root = await getRoot();
    if (root === null) {
        return null;
    }
    const segments = segmentsOf(normPath);
    if (segments.length === 0) {
        throw enoent(normPath);
    }
    const parent = await resolveParent(root, segments, create, normPath);
    try {
        return await parent.getFileHandle(segments[segments.length - 1], {
            create,
        });
    } catch (err) {
        if (err?.name === "TypeMismatchError") {
            throw nodeError("EISDIR", -21, normPath, "is a directory");
        }
        throw enoent(normPath);
    }
}

async function getDirHandle(normPath, create) {
    const root = await getRoot();
    if (root === null) {
        return null;
    }
    const segments = segmentsOf(normPath);
    let dir = root;
    for (const seg of segments) {
        try {
            dir = await dir.getDirectoryHandle(seg, { create });
        } catch (err) {
            if (err?.name === "TypeMismatchError") {
                throw nodeError("ENOTDIR", -20, normPath, "not a directory");
            }
            throw enoent(normPath);
        }
    }
    return dir;
}

async function stat(normPath) {
    // try as a file first, then as a directory
    const segments = segmentsOf(normPath);
    if (segments.length === 0) {
        return { isDir: true, size: 0, mtimeMs: 0 };
    }
    const root = await getRoot();
    if (root === null) {
        throw enoent(normPath);
    }
    let parent;
    try {
        parent = await resolveParent(root, segments, false, normPath);
    } catch (err) {
        throw enoent(normPath);
    }
    const name = segments[segments.length - 1];
    try {
        const fh = await parent.getFileHandle(name, { create: false });
        const file = await fh.getFile();
        return { isDir: false, size: file.size, mtimeMs: file.lastModified };
    } catch (err) {
        // not a file — try directory
    }
    try {
        await parent.getDirectoryHandle(name, { create: false });
        return { isDir: true, size: 0, mtimeMs: 0 };
    } catch (err) {
        throw enoent(normPath);
    }
}

async function readFile(normPath) {
    const fh = await getFileHandle(normPath, false);
    if (fh === null) {
        throw enoent(normPath);
    }
    const file = await fh.getFile();
    return new Uint8Array(await file.arrayBuffer());
}

async function writeFile(normPath, data) {
    const fh = await getFileHandle(normPath, true);
    if (fh === null) {
        throw enoent(normPath);
    }
    const writable = await fh.createWritable();
    try {
        const bytes =
            typeof data === "string" ? new TextEncoder().encode(data) : data;
        await writable.write(bytes);
    } finally {
        await writable.close();
    }
}

async function mkdir(normPath) {
    await getDirHandle(normPath, true);
}

async function readdir(normPath, withFileTypes) {
    let dir;
    try {
        dir = await getDirHandle(normPath, false);
    } catch (err) {
        // missing dir lists as empty, matching the no-filesystem baseline
        return [];
    }
    if (dir === null) {
        return [];
    }
    const entries = [];
    for await (const [name, handle] of dir.entries()) {
        entries.push(
            withFileTypes
                ? {
                      name,
                      isDirectory: () => handle.kind === "directory",
                      isFile: () => handle.kind === "file",
                      isSymbolicLink: () => false,
                  }
                : name,
        );
    }
    if (withFileTypes) {
        entries.sort((a, b) => (a.name < b.name ? -1 : 1));
    } else {
        entries.sort();
    }
    return entries;
}

async function remove(normPath) {
    const root = await getRoot();
    if (root === null) {
        return;
    }
    const segments = segmentsOf(normPath);
    if (segments.length === 0) {
        return;
    }
    let parent;
    try {
        parent = await resolveParent(root, segments, false, normPath);
    } catch (err) {
        return; // parent missing -> nothing to remove
    }
    try {
        await parent.removeEntry(segments[segments.length - 1], {
            recursive: true,
        });
    } catch (err) {
        // already gone -> success
    }
}

async function copyTree(fromNorm, toNorm) {
    const info = await stat(fromNorm).catch(() => null);
    if (info === null) {
        throw enoent(fromNorm);
    }
    if (info.isDir) {
        await mkdir(toNorm);
        for (const name of await readdir(fromNorm, false)) {
            await copyTree(`${fromNorm}/${name}`, `${toNorm}/${name}`);
        }
    } else {
        await writeFile(toNorm, await readFile(fromNorm));
    }
}

async function rename(fromNorm, toNorm) {
    await copyTree(fromNorm, toNorm);
    await remove(fromNorm);
}

module.exports = {
    available,
    stat,
    readFile,
    writeFile,
    mkdir,
    readdir,
    remove,
    rename,
    enoent,
    /** Wipe the whole persistent tree — used by tests for a clean slate. */
    async _reset() {
        const root = await getRoot();
        if (root === null) {
            return;
        }
        for await (const name of root.keys()) {
            await root.removeEntry(name, { recursive: true }).catch(() => {});
        }
    },
};
